import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, Modale, Paginazione, Riquadro, Scelta, Tabella, totaleDi,
} from '../../componenti/base.tsx'
import { IconaCarica, IconaScarica } from '../../componenti/Icone.tsx'
import { FotoDocumento } from '../../componenti/FotoDocumento.tsx'
import { caricaDocumento } from '../../servizi/documenti.ts'
import { ETICHETTA_IN_VERIFICA, attesaAntivirus } from '../../servizi/regole-documenti.ts'
import { data, leggibile } from './comune.ts'

type Documento = {
  id: string; nome: string; tipo: string; mime: string; dimensione: number
  caricato_il: string; classe_dato: string; antivirus: string
  versione: number; nota: string | null; data_documento: string | null
  sostituisce: string | null; scadenza: string | null
  /** Documenti della scheda prima della finestra. */
  totale?: string | number
}

type TipoDocumento = {
  codice: string; nome: string; classe_dato_minima: string
  solo_anteprima_fuori_carico: boolean
  /** Vale come modulo firmato dei consensi (474): si collega dalla sezione Consensi. */
  modulo_consensi?: boolean
  /** Documento d'identita' o di soggiorno (481): non si carica sulle schede con dati anonimizzati. */
  identifica_persona?: boolean
  /** Modulo del destinatario di un progetto, firmato (492): si carica dalla sezione «Moduli dei progetti». */
  modulo_progetto?: boolean
}

type Versione = {
  id: string; nome: string; tipo: string; versione: number
  caricato_il: string; cancellato: boolean
}

/** Documenti per pagina. */
const LIMITE_DOCUMENTI = 25

/** Stato del modulo di caricamento. Vuoto quando il modale e' chiuso. */
type Bozza = {
  file: File
  tipo: string
  data_documento: string
  scadenza: string
  nota: string
  /** Documento di cui questo e' la versione nuova. */
  sostituisce: string
}

export function Documenti({ personaId, tipoDocumento, anonimizzata = false }: {
  personaId: string; tipoDocumento?: string | null
  /** Scheda con dati anonimizzati (481): niente documenti d'identita' ne' moduli firmati, niente foto. */
  anonimizzata?: boolean
}) {
  const { puo } = useSessione()
  const ingressoFile = useRef<HTMLInputElement>(null)
  const [documenti, setDocumenti] = useState<Documento[] | null>(null)
  const [tipi, setTipi] = useState<TipoDocumento[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [caricando, setCaricando] = useState(false)
  const [salta, setSalta] = useState(0)
  const [totaleNoto, setTotaleNoto] = useState<number | null>(null)
  const [bozza, setBozza] = useState<Bozza | null>(null)
  const [erroreBozza, setErroreBozza] = useState<string | null>(null)
  const [versioni, setVersioni] = useState<{ di: Documento; righe: Versione[] } | null>(null)
  const [daCancellare, setDaCancellare] = useState<Documento | null>(null)
  const [motivo, setMotivo] = useState('')
  /** Esito dell'ultimo caricamento: il file e' salvato, e quando si potra' scaricare. */
  const [caricato, setCaricato] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<Documento[]>(
      `/persone/${personaId}/documenti?limite=${LIMITE_DOCUMENTI}&salta=${salta}`)
      .then((righe) => {
        setDocumenti(righe)
        const letto = totaleDi(righe)
        if (letto !== null) setTotaleNoto(letto)
      })
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [personaId, salta])

  useEffect(carica, [carica])

  // Il catalogo dei tipi dell'archivio non sta fra i cataloghi generali: e' una lista propria dei
  // documenti (ref.tipo_documento_archivio, migrazione 443) e si legge una volta sola.
  useEffect(() => {
    api.get<TipoDocumento[]>('/documenti/tipi').then(setTipi).catch(() => setTipi([]))
  }, [])

  const nomeTipo = (codice: string) => tipi.find((t) => t.codice === codice)?.nome ?? codice

  async function scarica(documento: Documento) {
    try {
      const { url } = await api.get<{ url: string }>(`/persone/${personaId}/documenti/${documento.id}/scarica`)
      window.open(url, '_blank', 'noopener')
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
  }

  async function apriVersioni(documento: Documento) {
    setErrore(null)
    try {
      const righe = await api.get<Versione[]>(
        `/persone/${personaId}/documenti/${documento.id}/versioni`)
      setVersioni({ di: documento, righe })
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
  }

  // Il documento appena caricato e' il piu' recente: sta in testa, quindi si torna alla prima
  // pagina. Se ci si trovava gia' li', `carica` ricarica comunque. Compare subito come ricevuto e
  // in verifica antivirus, e si scarica dopo la verifica (rotte/documenti.ts, osservazioni n. 4).
  const primaPagina = () => { if (salta === 0) carica(); else setSalta(0) }

  /**
   * Caricamento in tre tempi (servizi/documenti.ts): prenota, invia il file all'archivio, conferma
   * con l'impronta. E' lo stesso percorso della foto del documento, che resta com'era (OSS-10):
   * quella scatta e carica in un gesto solo, questo chiede prima tipo, data e nota.
   */
  async function invia() {
    if (!bozza) return
    if (!bozza.tipo) { setErroreBozza('Scegli il tipo di documento.'); return }
    setCaricando(true); setErroreBozza(null); setCaricato(null)
    try {
      await caricaDocumento(personaId, {
        nome: bozza.file.name, tipo: bozza.tipo, dati: bozza.file,
        data_documento: bozza.data_documento || null,
        scadenza: bozza.scadenza || null,
        nota: bozza.nota.trim() || null,
        sostituisce: bozza.sostituisce || null,
      })
      setBozza(null)
      // «Si caricano ma non salvano» (osservazioni n. 4, punto 13): il file e' salvato, e lo si dice.
      setCaricato(`«${bozza.file.name}» caricato e salvato. ${attesaAntivirus(new Date()).verifica}`)
      primaPagina()
    } catch (e) {
      setErroreBozza(e instanceof ErroreApi ? e.message
        : `Caricamento non riuscito: ${(e as Error).message}`)
    } finally { setCaricando(false) }
  }

  async function cancella() {
    if (!daCancellare) return
    setCaricando(true)
    try {
      await api.elimina(`/persone/${personaId}/documenti/${daCancellare.id}`, { motivo })
      setDaCancellare(null); setMotivo('')
      carica()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
    } finally { setCaricando(false) }
  }

  if (errore && !documenti) return <Avviso tipo="errore">{errore}</Avviso>
  if (!documenti) return <Attesa testo="Caricamento…" />

  // 481: sulla scheda anonimizzata i tipi che identificano la persona non si propongono (l'API li rifiuta).
  // 492: il modulo di progetto firmato chiede progetto e data della firma, e si carica dalla sezione «Moduli dei progetti».
  const opzioniTipo = tipi.filter((t) => !t.modulo_progetto && (!anonimizzata || !(t.identifica_persona || t.modulo_consensi)))
    .map((t) => ({ valore: t.codice, etichetta: t.nome }))
  const moduloConsensi = !!bozza && !!tipi.find((t) => t.codice === bozza.tipo)?.modulo_consensi
  const adesso = new Date()
  // Si sostituisce un documento non cancellato e non gia' sostituito da un altro.
  const sostituibili = documenti.filter((d) => !documenti.some((x) => x.sostituisce === d.id))

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div role="status">{caricato && <Avviso tipo="successo">{caricato}</Avviso>}</div>

      {/* Senza documento.carica in nessun incarico l'API risponde «non trovato»: i comandi non si offrono. */}
      {puo('documento.carica') && (
        <Riquadro titolo="Caricamento di un documento">
          <div className="persona-caricamento">
            <input ref={ingressoFile} type="file" hidden disabled={caricando}
                   onChange={(e) => {
                     const f = e.target.files?.[0]
                     e.target.value = ''
                     if (f) {
                       setErroreBozza(null)
                       setBozza({
                         file: f,
                         tipo: !anonimizzata && tipi.some((t) => t.codice === tipoDocumento) ? tipoDocumento! : '',
                         data_documento: '', scadenza: '', nota: '', sostituisce: '',
                       })
                     }
                   }} />
            {/* Un bottone e non un <label>: il comando si raggiunge con il tabulatore. */}
            <Bottone variante="secondario" icona={<IconaCarica />} inCorso={caricando}
                     onClick={() => ingressoFile.current?.click()}>
              Scegli un file
            </Bottone>
            {!anonimizzata && (
              <FotoDocumento personaId={personaId} tipoIniziale={tipoDocumento}
                             caricata={() => { setCaricato(null); primaPagina() }} />
            )}
          </div>
          {anonimizzata && (
            <p className="piccolo tenue persona-caricamento-aiuto">
              Scheda con dati anonimizzati: documenti d’identità, di soggiorno e moduli firmati non si caricano.
            </p>
          )}
          <p className="piccolo tenue persona-caricamento-aiuto">
            PDF, immagini e documenti fino a 50 MB. Dopo la scelta del file si indicano tipo, data e
            nota. La foto del documento si riduce a 1600 px sul lato lungo, in JPEG, senza la
            posizione dello scatto. Il file è salvato appena caricato e compare subito nell’elenco; si
            scarica dopo il controllo antivirus, in orario di servizio entro un’ora circa.
          </p>
        </Riquadro>
      )}

      <Riquadro denso>
        <Tabella
          intestazioni={['Nome', 'Tipo', 'Ver.', 'Nota', 'Dimensione', 'Data',
            <span key="azioni" className="solo-lettore">Azioni</span>]}
          vuoto={<Vuoto testo="Nessun documento sulla scheda." />}
        >
          {documenti.map((d) => (
            <tr key={d.id}>
              <td data-etichetta="Nome">{d.nome}</td>
              <td data-etichetta="Tipo">
                {nomeTipo(d.tipo)}
                {d.classe_dato !== 'personale' && <> <Etichetta tono="attenzione">
                  {leggibile(d.classe_dato)}
                </Etichetta></>}
                {d.antivirus === 'in_attesa' && <InVerifica caricatoIl={d.caricato_il} adesso={adesso} />}
                {d.antivirus !== 'pulito' && d.antivirus !== 'in_attesa' && <> <Etichetta tono="attenzione">
                  {leggibile(d.antivirus)}
                </Etichetta></>}
              </td>
              <td data-etichetta="Versione" className="mono">
                {d.versione > 1 || d.sostituisce
                  ? (
                    <Bottone variante="discreto" dimensione="piccolo"
                             onClick={() => void apriVersioni(d)}>v{d.versione}</Bottone>
                    )
                  : <span className="tenue">v{d.versione}</span>}
              </td>
              <td data-etichetta="Nota" className="documenti-nota" title={d.nota ?? undefined}>
                {d.nota ?? <span className="tenue">—</span>}
              </td>
              <td data-etichetta="Dimensione" className="mono">{(d.dimensione / 1024).toFixed(0)} kB</td>
              <td data-etichetta="Data">
                {d.data_documento ? data(d.data_documento) : data(d.caricato_il)}
                {!d.data_documento && <span className="tenue piccolo"> (caricato)</span>}
              </td>
              <td data-etichetta="Comandi">
                {/* In verifica non si scarica: l'API risponderebbe 409, e la riga dice gia' quando. */}
                {d.antivirus !== 'in_attesa' && (
                  <Bottone variante="secondario" dimensione="piccolo" icona={<IconaScarica />}
                           onClick={() => void scarica(d)}>Scarica</Bottone>
                )}
                {puo('documento.cancella') && (
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => { setDaCancellare(d); setMotivo('') }}>Cancella</Bottone>
                )}
              </td>
            </tr>
          ))}
        </Tabella>
        <Paginazione salta={salta} limite={LIMITE_DOCUMENTI}
                     totale={totaleDi(documenti) ?? totaleNoto}
                     ricevute={documenti.length} cambia={setSalta} />
      </Riquadro>

      {bozza && (
        <Modale
          titolo="Caricamento di un documento"
          chiudi={() => { if (!caricando) setBozza(null) }}
          azioni={
            <>
              <Bottone variante="secondario" onClick={() => setBozza(null)} disabled={caricando}>
                Annulla
              </Bottone>
              <Bottone onClick={() => void invia()} inCorso={caricando}>
                {caricando ? 'Caricamento…' : 'Carica'}
              </Bottone>
            </>
          }
        >
          <p className="piccolo tenue">{bozza.file.name} · {(bozza.file.size / 1024).toFixed(0)} kB</p>
          {erroreBozza && <p className="campo-errore" role="alert">{erroreBozza}</p>}
          <Scelta etichetta="Tipo di documento" obbligatorio valore={bozza.tipo}
                  cambia={(v) => setBozza({ ...bozza, tipo: v })}
                  vuota="Scegli il tipo" opzioni={opzioniTipo}
                  aiuto={moduloConsensi
                    ? 'Vale come modulo firmato dei consensi: dopo il caricamento si collega ai consensi dalla sezione '
                      + 'Consensi della linguetta Anagrafica, con «Carica il modulo firmato» e «È già tra i documenti della scheda».'
                    : undefined} />
          <Campo etichetta="Data del documento" tipo="date" valore={bozza.data_documento}
                 cambia={(v) => setBozza({ ...bozza, data_documento: v })}
                 aiuto="La data scritta sul documento, non quella del caricamento." />
          <Campo etichetta="Scadenza" tipo="date" valore={bozza.scadenza}
                 cambia={(v) => setBozza({ ...bozza, scadenza: v })}
                 aiuto="Solo se il documento scade, per esempio un permesso di soggiorno." />
          <AreaTesto etichetta="Nota" valore={bozza.nota} righe={3} maxLength={2000}
                     cambia={(v) => setBozza({ ...bozza, nota: v })}
                     aiuto="Resta cifrata con la chiave della scheda." />
          {!!sostituibili.length && (
            <Scelta etichetta="Sostituisce" valore={bozza.sostituisce}
                    cambia={(v) => setBozza({ ...bozza, sostituisce: v })}
                    vuota="Nessuno: è un documento nuovo"
                    aiuto="Il documento scelto resta nell’archivio come versione precedente."
                    opzioni={sostituibili.map((d) => ({
                      valore: d.id, etichetta: `${d.nome} — ${nomeTipo(d.tipo)} (v${d.versione})`,
                    }))} />
          )}
        </Modale>
      )}

      {versioni && (
        <Modale titolo={`Versioni di ${versioni.di.nome}`} chiudi={() => setVersioni(null)}>
          <Tabella intestazioni={['Ver.', 'Nome', 'Tipo', 'Caricato']}
                   vuoto={<Vuoto testo="Nessuna versione." />}>
            {versioni.righe.map((v) => (
              <tr key={v.id}>
                <td data-etichetta="Versione" className="mono">v{v.versione}</td>
                <td data-etichetta="Nome">
                  {v.nome}
                  {v.cancellato && <> <Etichetta tono="attenzione">cancellata</Etichetta></>}
                </td>
                <td data-etichetta="Tipo">{nomeTipo(v.tipo)}</td>
                <td data-etichetta="Caricato">{data(v.caricato_il)}</td>
              </tr>
            ))}
          </Tabella>
        </Modale>
      )}

      {daCancellare && (
        <Modale
          titolo="Cancellazione di un documento"
          chiudi={() => { if (!caricando) setDaCancellare(null) }}
          azioni={
            <>
              <Bottone variante="secondario" onClick={() => setDaCancellare(null)} disabled={caricando}>
                Annulla
              </Bottone>
              <Bottone variante="critico" onClick={() => void cancella()}
                       inCorso={caricando} disabled={motivo.trim().length < 5}>
                Cancella
              </Bottone>
            </>
          }
        >
          <p className="piccolo">
            {daCancellare.nome} — {nomeTipo(daCancellare.tipo)}. Il documento esce dall’elenco e
            resta nel registro con il motivo.
          </p>
          <AreaTesto etichetta="Motivo" obbligatorio valore={motivo} cambia={setMotivo}
                     righe={2} maxLength={300}
                     aiuto="Almeno cinque caratteri. Resta cifrato nel registro degli eventi." />
        </Modale>
      )}
    </>
  )
}

/**
 * Documento ricevuto e in attesa della verifica antivirus: l'etichetta e quando sara' disponibile,
 * oppure che la verifica e' in ritardo (servizi/regole-documenti.ts).
 */
function InVerifica({ caricatoIl, adesso }: { caricatoIl: string; adesso: Date }) {
  const attesa = attesaAntivirus(caricatoIl, adesso)
  return (
    <>
      {' '}<Etichetta tono={attesa.inRitardo ? 'attenzione' : 'informativo'}>{ETICHETTA_IN_VERIFICA}</Etichetta>
      <span className="piccolo tenue">
        {attesa.inRitardo
          ? ' ricevuto; la verifica è in ritardo: se domani non è disponibile, avvisa l’amministratore'
          : ` ricevuto, disponibile ${attesa.quando}`}
      </span>
    </>
  )
}
